import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import {
  ANNOUNCEMENT_SEVERITIES,
  CONTENT_LIMITS,
  canPerform,
  utcIsoToKabulDatetimeLocal,
  type AnnouncementSeverity,
  type TargetScope,
} from "@vital/shared";
import { AnnouncementPreview } from "../components/AnnouncementPreview";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/Card";
import { MetricCard } from "../components/MetricCard";
import { InspectPanel } from "../components/InspectPanel";
import { ContentDiffPanel } from "../components/ContentDiffPanel";
import { PasswordConfirmModal } from "../components/PasswordConfirmModal";
import { TargetFields, targetingPayload, formatTargetLabel } from "../components/TargetFields";
import {
  ScheduleFields,
  formatKabulDisplay,
  schedulePayloadFromLocal,
} from "../components/ScheduleFields";
import {
  AnnouncementInspectSummary,
  AnnouncementsFilterBar,
  AnnouncementsInventoryList,
} from "../components/announcements-inventory";
import { OpsEmptyState, SectionHeader } from "../components/ops-display";
import {
  ComposerHeader,
  ComposerPanel,
  ComposerSection,
  ComposerWorkspace,
} from "../components/ComposerChrome";
import { StickyFormActions } from "../components/StickyFormActions";
import { cmsStrings } from "../terminology";
import { useAuth } from "../context/AuthContext";
import { api, ApiError, type ContentDraftDiff } from "../lib/api";
import { useInspectSearchParam } from "../lib/use-inspect-search-param";
import { FILTER_ALL } from "../lib/content-state";
import {
  announcementSeverityLabelFa,
  filterAndSortAnnouncements,
  type AnnouncementSortKey,
} from "../lib/announcements-ops";

type Site = { id: string; code: string; name: string };
type SiteGroup = {
  id: string;
  name: string;
  members: Array<{ siteId: string; siteCode: string; siteName: string }>;
};
type Announcement = {
  id: string;
  state: string;
  scope: string;
  title: string;
  body: string;
  severity: string;
  dismissible: boolean;
  priority: number;
  emergency: boolean;
  emergencyAudience: string | null;
  rejectionReason: string | null;
  submittedAt: string | null;
  pausedFromState: string | null;
  siteCode: string | null;
  siteName: string | null;
  groupId: string | null;
  groupName: string | null;
  startsAt: string | null;
  endsAt: string | null;
  createdAt: string;
  updatedAt: string;
};

type PasswordMode = "publish" | "archive" | "pause" | "unpause";

function defaultEmergencyEndsLocal(): string {
  return utcIsoToKabulDatetimeLocal(new Date(Date.now() + 4 * 60 * 60 * 1000).toISOString());
}

export function AnnouncementsPage() {
  const { user } = useAuth();
  const authInput = {
    platformRole: user?.platformRole ?? null,
    membershipRole: user?.membershipRole ?? null,
    legacyRole: user?.role ?? null,
  };
  const canEdit = canPerform({ ...authInput, permission: "content.edit" });
  const canPublish = canPerform({ ...authInput, permission: "content.publish" });
  const isPlatformOwner = user?.platformRole === "owner";

  const [sites, setSites] = useState<Site[]>([]);
  const [groups, setGroups] = useState<SiteGroup[]>([]);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [listFilter, setListFilter] = useState<string>(FILTER_ALL);
  const [listQuery, setListQuery] = useState("");
  const [listSort, setListSort] = useState<AnnouncementSortKey>("updated");
  const [selectedId, setSelectedId] = useInspectSearchParam();

  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [severity, setSeverity] = useState<AnnouncementSeverity>("info");
  const [dismissible, setDismissible] = useState(false);
  const [emergency, setEmergency] = useState(false);
  const [emergencyAudience, setEmergencyAudience] = useState<"organization" | "platform">(
    "organization",
  );
  const [siteCode, setSiteCode] = useState("herat-firdoosi-01");
  const [targetScope, setTargetScope] = useState<TargetScope>("site");
  const [groupId, setGroupId] = useState("");
  const [priority, setPriority] = useState(0);
  const [startsAtLocal, setStartsAtLocal] = useState("");
  const [endsAtLocal, setEndsAtLocal] = useState("");
  const [saving, setSaving] = useState(false);

  const [passwordModal, setPasswordModal] = useState<{
    mode: PasswordMode;
    announcement: Announcement;
  } | null>(null);
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirming, setConfirming] = useState(false);

  const [rejectModal, setRejectModal] = useState<Announcement | null>(null);
  const [rejectReason, setRejectReason] = useState("");
  const [rejecting, setRejecting] = useState(false);

  const [diffModal, setDiffModal] = useState<{
    announcement: Announcement;
    diff: ContentDraftDiff | null;
    loading: boolean;
  } | null>(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [siteList, groupList, list] = await Promise.all([
        api.listSites(),
        api.listSiteGroups(),
        api.listAnnouncements(),
      ]);
      setSites(siteList);
      setGroups(groupList);
      setAnnouncements(list);
      setSiteCode((current) =>
        siteList.length > 0 && !siteList.some((s) => s.code === current)
          ? siteList[0]!.code
          : current,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "بارگذاری ناموفق");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  useEffect(() => {
    if (emergency && !endsAtLocal) {
      setEndsAtLocal(defaultEmergencyEndsLocal());
    }
    if (emergency) {
      setDismissible(false);
    }
  }, [emergency, endsAtLocal]);

  const filtered = useMemo(
    () =>
      filterAndSortAnnouncements(announcements, {
        query: listQuery,
        listFilter,
        sort: listSort,
      }),
    [announcements, listFilter, listQuery, listSort],
  );

  const queueCount = announcements.filter((a) => a.state === "pending_approval").length;
  const pausedCount = announcements.filter((a) => a.state === "paused").length;
  const publishedCount = announcements.filter(
    (a) => a.state === "published" || a.state === "scheduled",
  ).length;

  const selectedAnnouncement = useMemo(
    () => announcements.find((a) => a.id === selectedId) ?? null,
    [announcements, selectedId],
  );

  useEffect(() => {
    if (loading) return;
    if (selectedId && !announcements.some((a) => a.id === selectedId)) {
      setSelectedId(null);
    }
  }, [announcements, loading, selectedId, setSelectedId]);

  useEffect(() => {
    if (!selectedId) return;
    function onKeyDown(e: KeyboardEvent) {
      if (e.key !== "Escape") return;
      if (passwordModal || rejectModal || diffModal) return;
      e.preventDefault();
      setSelectedId(null);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [selectedId, passwordModal, rejectModal, diffModal]);

  async function handleCreate(e: FormEvent) {
    e.preventDefault();
    if (!canEdit) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      if (emergency && !endsAtLocal) {
        throw new ApiError(cmsStrings.announcementsComposer.emergencyNeedsEnd, 400);
      }
      await api.createAnnouncement({
        title,
        body,
        severity,
        dismissible: emergency ? false : dismissible,
        emergency,
        emergencyAudience: emergency
          ? isPlatformOwner
            ? emergencyAudience
            : "organization"
          : "organization",
        ...targetingPayload(targetScope, siteCode, groupId, priority),
        ...schedulePayloadFromLocal(startsAtLocal, endsAtLocal),
      });
      setTitle("");
      setBody("");
      setDismissible(false);
      setSeverity("info");
      setEmergency(false);
      setEmergencyAudience("organization");
      setStartsAtLocal("");
      setEndsAtLocal("");
      setSuccess(emergency ? cmsStrings.announcementsComposer.emergencyDraftSaved : cmsStrings.announcementsComposer.draftSaved);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ذخیره ناموفق");
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(a: Announcement) {
    setError(null);
    try {
      await api.submitAnnouncement(a.id);
      setSuccess(`«${a.title}» برای تأیید ارسال شد.`);
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "ارسال برای تأیید ناموفق");
    }
  }

  async function handleReject(e: FormEvent) {
    e.preventDefault();
    if (!rejectModal) return;
    setRejecting(true);
    setError(null);
    try {
      await api.rejectAnnouncement(rejectModal.id, rejectReason.trim());
      setSuccess(`«${rejectModal.title}» رد شد.`);
      setRejectModal(null);
      setRejectReason("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "رد کردن ناموفق");
    } finally {
      setRejecting(false);
    }
  }

  async function openDiff(a: Announcement) {
    setDiffModal({ announcement: a, diff: null, loading: true });
    try {
      const diff = await api.getAnnouncementDiff(a.id);
      setDiffModal({ announcement: a, diff, loading: false });
    } catch (err) {
      setDiffModal(null);
      setError(err instanceof ApiError ? err.message : "مقایسه ناموفق");
    }
  }

  async function handleConfirm(e: FormEvent) {
    e.preventDefault();
    if (!passwordModal) return;
    setConfirming(true);
    setError(null);
    try {
      const { mode, announcement: a } = passwordModal;
      if (mode === "publish") {
        await api.publishAnnouncement(a.id, confirmPassword);
        setSuccess(
          a.emergency
            ? `اطلاعیه اضطراری «${a.title}» منتشر شد.`
            : a.startsAt
              ? `«${a.title}» زمان‌بندی شد.`
              : `«${a.title}» منتشر شد.`,
        );
      } else if (mode === "archive") {
        await api.archiveAnnouncement(a.id, confirmPassword);
        setSuccess(`«${a.title}» بایگانی شد.`);
      } else if (mode === "pause") {
        await api.pauseAnnouncement(a.id, confirmPassword);
        setSuccess(`«${a.title}» متوقف شد.`);
      } else {
        await api.unpauseAnnouncement(a.id, confirmPassword);
        setSuccess(`«${a.title}» از توقف خارج شد.`);
      }
      setPasswordModal(null);
      setConfirmPassword("");
      await loadData();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "عملیات ناموفق");
    } finally {
      setConfirming(false);
    }
  }

  const c = cmsStrings.announcementsComposer;
  const passwordTitles: Record<PasswordMode, string> = {
    publish: c.publishConfirmTitle,
    archive: c.archiveConfirmTitle,
    pause: c.pauseConfirmTitle,
    unpause: c.resumeConfirmTitle,
  };
  const passwordLabels: Record<PasswordMode, string> = {
    publish: c.publishConfirmLabel,
    archive: c.archiveConfirmLabel,
    pause: c.pauseConfirmLabel,
    unpause: c.resumeConfirmLabel,
  };
  const passwordImpacts: Record<PasswordMode, string> = {
    publish: c.publishImpact,
    archive: c.archiveImpact,
    pause: c.pauseImpact,
    unpause: c.resumeImpact,
  };

  function renderAnnouncementActions(a: Announcement) {
    return (
      <>
        <button type="button" className="btn btn--ghost btn--sm" onClick={() => void openDiff(a)}>
          {c.compare}
        </button>
        {a.state === "draft" && (
          <>
            {canEdit && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => void handleSubmit(a)}
              >
                {c.submitForApproval}
              </button>
            )}
            {canPublish && (
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => {
                  setPasswordModal({ mode: "publish", announcement: a });
                  setConfirmPassword("");
                  setError(null);
                }}
              >
                {c.publish}
              </button>
            )}
            {canPublish && (
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => {
                  setPasswordModal({ mode: "archive", announcement: a });
                  setConfirmPassword("");
                }}
              >
                {c.deleteDraft}
              </button>
            )}
          </>
        )}
        {a.state === "pending_approval" && canPublish && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setRejectModal(a);
                setRejectReason("");
              }}
            >
              {c.reject}
            </button>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              onClick={() => {
                setPasswordModal({ mode: "publish", announcement: a });
                setConfirmPassword("");
              }}
            >
              {c.approveAndPublish}
            </button>
          </>
        )}
        {(a.state === "published" || a.state === "scheduled") && canPublish && (
          <>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setPasswordModal({ mode: "pause", announcement: a });
                setConfirmPassword("");
              }}
            >
              {c.pause}
            </button>
            <button
              type="button"
              className="btn btn--ghost btn--sm"
              onClick={() => {
                setPasswordModal({ mode: "archive", announcement: a });
                setConfirmPassword("");
              }}
            >
              {c.archive}
            </button>
          </>
        )}
        {a.state === "paused" && canPublish && (
          <button
            type="button"
            className="btn btn--primary btn--sm"
            onClick={() => {
              setPasswordModal({ mode: "unpause", announcement: a });
              setConfirmPassword("");
            }}
          >
            {c.resume}
          </button>
        )}
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={cmsStrings.nav.announcements}
        actions={
          canEdit ? (
            <a href="#announcement-composer" className="btn btn--primary btn--sm">
              {cmsStrings.announcementsComposer.createDraftCta}
            </a>
          ) : undefined
        }
      />

      {error && (
        <p className="banner banner--error" role="alert">
          {error}
        </p>
      )}
      {success && (
        <p className="banner banner--success" role="status">
          {success}
        </p>
      )}

      <div className="metric-strip" data-vt-inventory-metrics aria-label={cmsStrings.announcementsUi.listTitle}>
        <MetricCard
          label={cmsStrings.inventory.filterAll}
          value={announcements.length}
          loading={loading}
        />
        <MetricCard
          label={cmsStrings.inventory.filterQueue}
          value={queueCount}
          loading={loading}
          tone={queueCount > 0 ? "warning" : "neutral"}
        />
        <MetricCard
          label={cmsStrings.overviewUi.publishedOrScheduled}
          value={publishedCount}
          loading={loading}
          sublabel={
            pausedCount > 0
              ? `${cmsStrings.inventory.filterPaused}: ${pausedCount}`
              : undefined
          }
        />
      </div>

      <div className="page-stack page-stack--inventory">
      <div
        className={`content-ops-layout${selectedAnnouncement ? " content-ops-layout--inspect-open" : ""}`}
        data-vt-content-ops
        data-vt-inspect-open={selectedAnnouncement ? "true" : "false"}
      >
        <Card className="content-ops-layout__list inventory-surface" bodyClassName="card__body--flush">
          <SectionHeader title={cmsStrings.announcementsUi.listTitle} />
          <AnnouncementsFilterBar
            listFilter={listFilter}
            onListFilterChange={setListFilter}
            queueCount={queueCount}
            query={listQuery}
            onQueryChange={setListQuery}
            sort={listSort}
            onSortChange={setListSort}
          />
          {loading ? (
            <div className="inventory-skeleton" aria-busy="true" aria-label={cmsStrings.shell.loading}>
              <div className="inventory-skeleton__row" style={{ width: "92%" }} />
              <div className="inventory-skeleton__row" style={{ width: "78%" }} />
              <div className="inventory-skeleton__row" style={{ width: "85%" }} />
              <div className="inventory-skeleton__row" style={{ width: "70%" }} />
              <div className="inventory-skeleton__row" style={{ width: "88%" }} />
            </div>
          ) : announcements.length === 0 ? (
            <OpsEmptyState title={cmsStrings.announcementsUi.emptyTitle} />
          ) : filtered.length === 0 ? (
            <OpsEmptyState title={cmsStrings.inventory.noMatchTitle} />
          ) : (
            <AnnouncementsInventoryList
              rows={filtered}
              selectedId={selectedId}
              onSelect={(id) => setSelectedId((current) => (current === id ? null : id))}
              renderActions={(row) => renderAnnouncementActions(row as Announcement)}
            />
          )}
        </Card>

        {selectedAnnouncement && (
          <InspectPanel
            title={cmsStrings.announcementsUi.inspectTitle}
            className="content-inspect-panel"
            onClose={() => setSelectedId(null)}
            closeLabel={cmsStrings.announcementsComposer.close}
            footer={renderAnnouncementActions(selectedAnnouncement)}
          >
            <AnnouncementInspectSummary item={selectedAnnouncement} />
          </InspectPanel>
        )}
      </div>

        {canEdit && (
        <ComposerWorkspace id="announcement-composer">
          <ComposerPanel>
            <ComposerHeader
              kind={cmsStrings.composerChrome.kindAnnouncement}
              title={cmsStrings.announcementsComposer.cardTitle}
              statusLabel={cmsStrings.composerChrome.statusCreate}
              statusTone="info"
            />
            <form
              className="content-form content-form--in-card composer-form composer-form--announcement"
              onSubmit={handleCreate}
            >
              <ComposerSection
                id="announcement-section-basic"
                title={cmsStrings.composerChrome.sectionBasic}
                bare
              >
                <label>
                  <span className="composer-label">
                    {cmsStrings.announcementsComposer.titleLabel}
                    <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                      {title.length}/{CONTENT_LIMITS.titleMax}
                    </bdi>
                  </span>
                  <input
                    className="composer-title-input"
                    value={title}
                    onChange={(e) => setTitle(e.target.value.slice(0, CONTENT_LIMITS.titleMax))}
                    required
                    maxLength={CONTENT_LIMITS.titleMax}
                  />
                </label>
                <label>
                  <span className="composer-label">
                    {cmsStrings.announcementsComposer.bodyLabel}
                    <bdi dir="ltr" className="composer-label__count ltr-isolate latin-digits">
                      {body.length}/{CONTENT_LIMITS.bodyMax}
                    </bdi>
                  </span>
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value.slice(0, CONTENT_LIMITS.bodyMax))}
                    required
                    maxLength={CONTENT_LIMITS.bodyMax}
                    rows={3}
                  />
                </label>
                <fieldset className="composer-severity">
                  <legend>{cmsStrings.announcementsComposer.severityLabel}</legend>
                  <div className="composer-severity__options" role="radiogroup">
                    {ANNOUNCEMENT_SEVERITIES.map((s) => (
                      <label
                        key={s}
                        className={`composer-severity__option composer-severity__option--${s}${
                          severity === s ? " is-selected" : ""
                        }`}
                      >
                        <input
                          type="radio"
                          className="visually-hidden"
                          name="announcement-severity"
                          value={s}
                          checked={severity === s}
                          onChange={() => setSeverity(s)}
                        />
                        <span>{announcementSeverityLabelFa(s)}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <div id="announcement-section-emergency" className="composer-flags">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={dismissible}
                      disabled={emergency}
                      onChange={(e) => setDismissible(e.target.checked)}
                    />
                    <span>{cmsStrings.announcementsComposer.dismissible}</span>
                  </label>
                  <label className={`checkbox-label${emergency ? " checkbox-label--emergency" : ""}`}>
                    <input
                      type="checkbox"
                      checked={emergency}
                      onChange={(e) => setEmergency(e.target.checked)}
                    />
                    <span>{cmsStrings.announcementsComposer.emergencyCheckbox}</span>
                  </label>
                </div>
                {emergency && isPlatformOwner && (
                  <div className="composer-emergency">
                    <label>
                      <span>{cmsStrings.announcementsComposer.emergencyAudience}</span>
                      <select
                        value={emergencyAudience}
                        onChange={(e) =>
                          setEmergencyAudience(e.target.value as "organization" | "platform")
                        }
                      >
                        <option value="organization">
                          {cmsStrings.announcementsComposer.emergencyOrgOnly}
                        </option>
                        <option value="platform">
                          {cmsStrings.announcementsComposer.emergencyPlatform}
                        </option>
                      </select>
                    </label>
                  </div>
                )}
              </ComposerSection>
              <ComposerSection
                id="announcement-section-targeting"
                title={cmsStrings.composerChrome.sectionTargeting}
                bare
              >
                <TargetFields
                  compact
                  sites={sites}
                  groups={groups}
                  targetScope={targetScope}
                  siteCode={siteCode}
                  groupId={groupId}
                  priority={priority}
                  onTargetScopeChange={setTargetScope}
                  onSiteCodeChange={setSiteCode}
                  onGroupIdChange={setGroupId}
                  onPriorityChange={setPriority}
                  labels={{
                    ...cmsStrings.contentTargeting,
                    globalHint: "",
                    groupEmptyHint: "",
                  }}
                />
              </ComposerSection>
              <ComposerSection
                id="announcement-section-schedule"
                title={cmsStrings.composerChrome.sectionSchedule}
                bare
              >
                <ScheduleFields
                  compact
                  startsAtLocal={startsAtLocal}
                  endsAtLocal={endsAtLocal}
                  onStartsAtChange={setStartsAtLocal}
                  onEndsAtChange={setEndsAtLocal}
                  legend={cmsStrings.announcementsComposer.scheduleLegend}
                  hint={null}
                />
              </ComposerSection>
              <StickyFormActions>
                <button type="submit" className="btn btn--primary" disabled={saving}>
                  {saving
                    ? cmsStrings.announcementsComposer.saving
                    : cmsStrings.announcementsComposer.saveDraft}
                </button>
              </StickyFormActions>
            </form>
          </ComposerPanel>

          <Card
            title={cmsStrings.announcementsComposer.previewTitle}
            className="preview-panel"
            bodyClassName="card__body--flush"
          >
            <AnnouncementPreview
              title={title}
              body={body}
              severity={severity}
              dismissible={emergency ? false : dismissible}
              emergency={emergency}
            />
          </Card>
        </ComposerWorkspace>
        )}
      </div>

      {passwordModal && (
        <PasswordConfirmModal
          title={passwordTitles[passwordModal.mode]}
          description={
            passwordModal.mode === "publish" && passwordModal.announcement.emergency
              ? cmsStrings.announcementsComposer.publishEmergencyImpact
              : passwordImpacts[passwordModal.mode]
          }
          summary={
            <dl className="publish-summary">
              <dt>{cmsStrings.announcementsComposer.summaryTitle}</dt>
              <dd>{passwordModal.announcement.title}</dd>
              <dt>{cmsStrings.announcementsComposer.summaryTarget}</dt>
              <dd>{formatTargetLabel(passwordModal.announcement)}</dd>
              <dt>{cmsStrings.inventory.factSchedule}</dt>
              <dd>
                <bdi dir="ltr" className="ltr-isolate">
                  {formatKabulDisplay(passwordModal.announcement.startsAt)} –{" "}
                  {formatKabulDisplay(passwordModal.announcement.endsAt)}
                </bdi>
              </dd>
              <dt>{cmsStrings.inventory.sortSeverity}</dt>
              <dd>
                {announcementSeverityLabelFa(passwordModal.announcement.severity)}
                {passwordModal.announcement.emergency
                  ? ` · ${cmsStrings.inventory.emergencyBadge}`
                  : ""}
              </dd>
            </dl>
          }
          password={confirmPassword}
          onPasswordChange={setConfirmPassword}
          confirming={confirming}
          confirmLabel={passwordLabels[passwordModal.mode]}
          tone={
            passwordModal.mode === "archive" || passwordModal.mode === "pause"
              ? "danger"
              : "primary"
          }
          onCancel={() => setPasswordModal(null)}
          onConfirm={handleConfirm}
        />
      )}

      {rejectModal && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setRejectModal(null)}
        >
          <div className="modal" role="dialog" onClick={(e) => e.stopPropagation()}>
            <h2>{cmsStrings.announcementsComposer.rejectDialogTitle}</h2>
            <p>
              «{rejectModal.title}» {cmsStrings.announcementsComposer.rejectDialogBody}
            </p>
            <form onSubmit={handleReject}>
              <label>
                <span>{cmsStrings.announcementsComposer.rejectReason}</span>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value.slice(0, 500))}
                  required
                  rows={3}
                  maxLength={500}
                />
              </label>
              <div className="modal-actions">
                <button type="button" className="btn btn--ghost" onClick={() => setRejectModal(null)}>
                  {cmsStrings.announcementsComposer.cancel}
                </button>
                <button type="submit" className="btn btn--danger" disabled={rejecting} aria-busy={rejecting}>
                  {rejecting
                    ? cmsStrings.announcementsComposer.rejecting
                    : cmsStrings.announcementsComposer.reject}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {diffModal && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setDiffModal(null)}
        >
          <div
            className="modal modal--wide"
            role="dialog"
            onClick={(e) => e.stopPropagation()}
          >
            <h2>{cmsStrings.announcementsComposer.diffTitlePrefix} — {diffModal.announcement.title}</h2>
            {diffModal.loading || !diffModal.diff ? (
              <p className="card__empty">{cmsStrings.shell.loading}</p>
            ) : (
              <ContentDiffPanel
                fields={diffModal.diff.fields}
                hasPublishedSnapshot={diffModal.diff.hasPublishedSnapshot}
                publishedVersion={diffModal.diff.publishedVersion}
                state={diffModal.diff.state}
              />
            )}
            <div className="modal-actions">
              <button type="button" className="btn btn--ghost" onClick={() => setDiffModal(null)}>
                {cmsStrings.announcementsComposer.close}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
